// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { assertWasmKernelContract, } from "./contract-validation.js";
import { createCoreBackedKernel } from "./core-backed.js";
import { createPureTsBoundaryReceipt, isWasmRuntimeSupported, loadNativeWasmKernel, } from "./native-wasm.js";
import { createTheoremAcceleratedKernel, getTheoremFidelityReport, } from "./theorem-accelerator.js";
import { assertWasmSeamBridgeContract, } from "./seam-bridge.js";
/**
 * Initialize a CSK kernel.
 *
 * - `"auto"` (default): Try WASM first, fall back to core-backed pure-TS.
 * - `"wasm"`: WASM only. Throws if unavailable.
 * - `"pure-ts"`: Core-backed pure TypeScript (uses `@csk/core` enforcement).
 *   No WASM binary required.
 * - `"theorem-hybrid"`: Use WASM for the five INERT/CONTAINED theorems
 *   (RES.CONSUME, RES.RELEASE, INV.ESTABLISH, INV.PRESERVE, COMPOSE) and
 *   TypeScript for the five IMMUNE theorems. Falls back to `pure-ts` if WASM
 *   is unavailable. See `getTheoremFidelityReport()` for per-theorem fidelity.
 *
 * @param backend — Which backend to use.
 * @param wasmUrl — Optional URL or path to the `.wasm` file.
 *   Defaults to looking for `csk_wasm_bg.wasm` relative to this module.
 */
export async function createKernel(backend = "auto", wasmUrl) {
    if (backend === "pure-ts") {
        return initCoreBackend();
    }
    if (backend === "wasm") {
        return initWasm(wasmUrl);
    }
    if (backend === "theorem-hybrid") {
        try {
            const wasmResult = await initWasm(wasmUrl);
            const coreResult = initCoreBackend();
            const kernel = createTheoremAcceleratedKernel(wasmResult.kernel, coreResult.kernel);
            const contractValidation = assertWasmKernelContract(kernel, "theorem-hybrid");
            const seamBridgeValidation = assertWasmSeamBridgeContract(kernel, "theorem-hybrid");
            // Combine theorem lists: WASM contributes CONTAINED/INERT; core contributes IMMUNE.
            // For now the WASM list covers all theorems; use it as the combined source.
            const theorems = wasmResult.theorems;
            return {
                kernel,
                backend: "theorem-hybrid",
                theorems,
                theoremFidelity: getTheoremFidelityReport("wasm"),
                contractValidation,
                seamBridgeValidation,
                nativeBoundary: wasmResult.nativeBoundary,
            };
        }
        catch {
            // WASM unavailable — fall back to pure-TS
            return initCoreBackend();
        }
    }
    // auto: try WASM, fall back to @csk/core-backed pure-TS
    try {
        return await initWasm(wasmUrl);
    }
    catch {
        return initCoreBackend();
    }
}
async function initWasm(wasmSource) {
    const { kernel, boundary } = await loadNativeWasmKernel(wasmSource);
    const contractValidation = assertWasmKernelContract(kernel, "wasm");
    const seamBridgeValidation = assertWasmSeamBridgeContract(kernel, "wasm");
    const theoremList = kernel.csk_available_theorems();
    const theorems = theoremList
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    return {
        kernel,
        backend: "wasm",
        theorems,
        theoremFidelity: getTheoremFidelityReport("wasm"),
        contractValidation,
        seamBridgeValidation,
        nativeBoundary: boundary,
    };
}
function initCoreBackend() {
    const kernel = createCoreBackedKernel();
    const contractValidation = assertWasmKernelContract(kernel, "pure-ts");
    const seamBridgeValidation = assertWasmSeamBridgeContract(kernel, "pure-ts");
    const theoremList = kernel.csk_available_theorems();
    const theorems = theoremList
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    return {
        kernel,
        backend: "pure-ts",
        theorems,
        theoremFidelity: getTheoremFidelityReport("pure-ts"),
        contractValidation,
        seamBridgeValidation,
        nativeBoundary: createPureTsBoundaryReceipt(),
    };
}
/**
 * Check whether WASM is available in this environment.
 *
 * Returns `true` if `WebAssembly` is defined and `.instantiate` exists.
 * This does NOT check whether the CSK WASM module is loadable — only
 * that the runtime supports WASM at all.
 */
export function isWasmSupported() {
    return isWasmRuntimeSupported();
}
//# sourceMappingURL=loader.js.map